/**
 * Remote Config keys.
 *
 * Mirrors Flutter's `RemoteConfigs` enum
 * (lib/domain/enums/remote_configs.dart:1-45) — all 43 members, declared in
 * Flutter's exact ordinal order.
 *
 * **Invariant: the Remote Config key string IS the Flutter enum member name,
 * verbatim.** Flutter reads every value as
 * `_remoteConfig.getX(RemoteConfigs.<member>.name)`
 * (lib/services/remote_config_service.dart:20-85), so the wire key is the
 * camelCase Dart identifier. Each TypeScript member therefore carries its own
 * name as its string value, and the members are spelled camelCase (rather than
 * this project's usual SCREAMING_CASE enum members) so that member and value
 * stay visibly identical — a mismatch here silently reads the wrong server key
 * on one client only.
 *
 * Adding a key: add it to Flutter's enum first, then mirror the member name
 * here character for character.
 */
export enum RemoteConfigs {
  budgetExceeded = 'budgetExceeded',
  initVideos = 'initVideos',
  useGoogleLoginOnApple = 'useGoogleLoginOnApple',
  isTestMode = 'isTestMode',
  defaultCurrency = 'defaultCurrency',
  dollarToCoinsRatio = 'dollarToCoinsRatio',
  coinsToDiamondsRatio = 'coinsToDiamondsRatio',
  diamondsToDollarsRatio = 'diamondsToDollarsRatio',
  withdrawMinDollars = 'withdrawMinDollars',
  withdrawMaxDollars = 'withdrawMaxDollars',
  withdrawAutoDollars = 'withdrawAutoDollars',
  exclusiveSubscriptionMinDollars = 'exclusiveSubscriptionMinDollars',
  exclusiveSubscriptionMaxDollars = 'exclusiveSubscriptionMaxDollars',
  exclusiveSubscriptionDefaultDollars = 'exclusiveSubscriptionDefaultDollars',
  contentUnlockMinDollars = 'contentUnlockMinDollars',
  contentUnlockMaxDollars = 'contentUnlockMaxDollars',
  contentUnlockDefaultDollars = 'contentUnlockDefaultDollars',
  versionGifts = 'versionGifts',
  versionRechargeWalletProducts = 'versionRechargeWalletProducts',
  versionPremiumPackages = 'versionPremiumPackages',
  versionDefaultSettings = 'versionDefaultSettings',
  versionTermsOfService = 'versionTermsOfService',
  versionCoinsPolicy = 'versionCoinsPolicy',
  versionCommunityGuidelines = 'versionCommunityGuidelines',
  versionExclusiveSubscriptionsPolicy = 'versionExclusiveSubscriptionsPolicy',
  versionPremiumSubscriptions = 'versionPremiumSubscriptions',
  versionPrivacyPolicy = 'versionPrivacyPolicy',
  versionRewardsPolicy = 'versionRewardsPolicy',
  preferredContentTagsWatchMinSeconds = 'preferredContentTagsWatchMinSeconds',
  preferredContentTagsWatchMinPercentage = 'preferredContentTagsWatchMinPercentage',
  lastSeenEnabled = 'lastSeenEnabled',
  versionDonationProducts = 'versionDonationProducts',
  updateViewsWatchMinSeconds = 'updateViewsWatchMinSeconds',
  saveToWatchHistoryMinSeconds = 'saveToWatchHistoryMinSeconds',
  filmMillisMin = 'filmMillisMin',
  contentMinSeconds = 'contentMinSeconds',
  contentMaxSeconds = 'contentMaxSeconds',
  freeContentMaxSeconds = 'freeContentMaxSeconds',
  premiumContentCoinsPerMinute = 'premiumContentCoinsPerMinute',
  contentScheduleMinHours = 'contentScheduleMinHours',
  contentScheduleMaxHours = 'contentScheduleMaxHours',
  boosts = 'boosts',
  shouldAutoInitVideos = 'shouldAutoInitVideos',
}

// TODO: @claude add a follow up task for this: integrate the 6 web-only Remote Config tokens into Flutter's RemoteConfigs enum — Flutter lib/domain/enums/remote_configs.dart:1-45
/**
 * Remote Config keys that exist on the web only — they have **no** counterpart
 * in Flutter's `RemoteConfigs` enum today. Kept in a separate enum so the
 * Flutter-parity set above stays a literal mirror of
 * lib/domain/enums/remote_configs.dart:1-45.
 *
 * All six are published in the live Remote Config console template, added by
 * hand under exactly the camelCase names below (confirmed by the operator,
 * user review round 1). A seventh key would need the same manual step: the
 * client can only publish defaults, never create server-side keys.
 */
export enum WebRemoteConfigs {
  /**
   * Selects the browser Fullscreen API over the in-page fullscreen container
   * (`presentation/feature/content/list/ContentListItem.tsx:97,296`,
   * `store/slices/videoManagement/videoManagementSlice.ts:130`).
   *
   * Cross-over verdict: **should NOT cross to Flutter** — it names a
   * browser-only rendering choice that has no Flutter concept.
   */
  webNativeFullscreenMode = 'webNativeFullscreenMode',
  /**
   * Max characters of a content share message
   * (`domain/usecases/share/generateUserContentShareDataUseCase.ts:21`).
   *
   * Cross-over verdict: **should cross to Flutter** — a platform-neutral
   * business limit Flutter currently hardcodes.
   */
  shareContentMaxCharacters = 'shareContentMaxCharacters',
  /**
   * Max characters of a user-profile share message
   * (`domain/usecases/share/generateUserProfileShareDataUseCase.ts:20`).
   *
   * Cross-over verdict: **should cross to Flutter** — a platform-neutral
   * business limit Flutter currently hardcodes.
   */
  shareUserMaxCharacters = 'shareUserMaxCharacters',
  /**
   * Minimum duration of a chat video, in seconds
   * (`presentation/feature/publish/.../hooks/useAddContent.ts:204`).
   *
   * Cross-over verdict: **should cross to Flutter** — a platform-neutral
   * business limit Flutter currently hardcodes.
   */
  chatContentMinSeconds = 'chatContentMinSeconds',
  /**
   * Maximum duration of a chat video, in seconds
   * (`presentation/feature/publish/.../hooks/useAddContent.ts:207`,
   * `.../components/resolveDropStepConfig.ts:67,78`).
   *
   * Cross-over verdict: **should cross to Flutter** — a platform-neutral
   * business limit Flutter currently hardcodes.
   */
  chatContentMaxSeconds = 'chatContentMaxSeconds',
  /**
   * Upload `typeLimit` byte threshold (`domain/utils/uploadFileInfoBuilder.ts:87`).
   *
   * Cross-over verdict: **should cross to Flutter** — a platform-neutral
   * business limit Flutter currently hardcodes.
   */
  contentTypeLimit = 'contentTypeLimit',
}

/**
 * Any Remote Config key the app may read — the Flutter-parity set plus the
 * web-only set. 43 + 6 = 49 members.
 *
 * `WEB_PLATFORM_CODE` from the retired `presentation/constants/remoteConfigConstants.ts`
 * is deliberately **not** here: it is a fixed protocol sentinel, not a Remote
 * Config concern, and moves to `COMMON_CONSTANTS` when that file is deleted.
 */
export type RemoteConfigKey = RemoteConfigs | WebRemoteConfigs;
